import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { IoMdPhonePortrait } from "react-icons/io";
import { FaFacebookF, FaLock, FaUser, FaInstagram, FaTiktok, FaSearch, FaChevronDown } from "react-icons/fa";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaHeart, FaCartShopping } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { get_card_products, get_wishlist_products } from "../store/reducers/cardReducer";
import useGuestId from "../hooks/useGuestId";
import { FaTimes, FaBars } from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import api from "../api/api";

const isExternalUrl = url => /^https?:\/\//i.test(String(url || ""));

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { currentCompany, navMenus } = useSelector(state => state.init);
  const headerTopBg = currentCompany?.headerTopBgColor || '#caddff';
  const { userInfo } = useSelector(state => state.auth);
  const guestId = useGuestId();
  const { card_product_count, wishlist_count } = useSelector(state => state.card);
  const { getBackground, getButtonStyles, getColor } = useTheme();
  const primaryColor = getColor("primary", "#ae1d22");

  const { pathname } = location;

  const [showSidebar, setShowSidebar] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [navKey, setNavKey] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isClearingSearch, setIsClearingSearch] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0, width: 0 });
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const [megaTop, setMegaTop] = useState(0);
  const debounceRef = useRef(null);
  const megaCloseRef = useRef(null);
  const navMenuRef = useRef(null);
  const searchContainerRef = useRef(null);
  const suggestionsDropdownRef = useRef(null);

  const setActiveSearchContainer = useCallback((element) => {
    const container = element?.closest("[data-search-container]");
    if (!container) return null;

    searchContainerRef.current = container;
    const rect = container.getBoundingClientRect();
    setDropdownPosition({
      left: rect.left,
      top: rect.bottom,
      width: rect.width
    });
    return container;
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      setActiveMegaMenu(null);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => () => clearTimeout(megaCloseRef.current), []);

  // Valores seguros con valores por defecto
  const phone = currentCompany?.phone || "";
  const phone_whatsApp = currentCompany?.phone_whatsApp || "";
  const logo = currentCompany?.logo || "";
  const name = currentCompany?.name || "Tienda";
  const isDevelopment = process.env.NODE_ENV === 'development';
  const dashboardUrl = isDevelopment ? 'http://localhost:3001' : (currentCompany?.UrlAdministracion || "#");

  // Función para verificar si un enlace está activo
  const isLinkActive = useCallback(
    linkTo => {
      // Crear URL para el link
      const linkUrl = new URL(linkTo, window.location.origin);
      const linkPath = linkUrl.pathname;
      const linkSearchParams = new URLSearchParams(linkUrl.search);

      // Si estamos en la misma ruta
      if (pathname === linkPath) {
        // Verificar parámetros para rutas de búsqueda
        if (pathname === "/products/search") {
          // Comparar cada parámetro del link con los actuales
          for (const [key, value] of linkSearchParams.entries()) {
            if (searchParams.get(key) !== value) {
              return false;
            }
          }
          return true;
        }
        // Para otras rutas sin parámetros
        return true;
      }
      return false;
    },
    [pathname, searchParams]
  );

  // Función para navegar que forzará la recarga
  const handleNavClick = useCallback(
    to => {
      // Incrementar la key para forzar recarga del componente
      setNavKey(prev => prev + 1);

      // Navegar a la nueva URL
      navigate(to);
    },
    [navigate]
  );

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = e => {
      const isInsideSearch = searchContainerRef.current?.contains(e.target);
      const isInsideSuggestions = suggestionsDropdownRef.current?.contains(e.target);

      if (!isInsideSearch && !isInsideSuggestions) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Actualizar posición en scroll
  useEffect(() => {
    if (!showSuggestions) return;
    
    const handleScroll = () => {
      if (showSuggestions) {
        setShowSuggestions(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showSuggestions]);

  // Actualizar posición del dropdown
  useEffect(() => {
    if (showSuggestions && suggestions.length > 0 && searchContainerRef.current) {
      const updatePosition = () => {
        const rect = searchContainerRef.current.getBoundingClientRect();
        if (rect.width === 0) return;
        setDropdownPosition({
          left: rect.left,
          top: rect.bottom,
          width: rect.width
        });
      };
      updatePosition();
      
      // Re-calcular en el siguiente frame para asegurar el layout
      requestAnimationFrame(updatePosition);
    }
  }, [showSuggestions, suggestions]);

  const handleSearchChange = useCallback(e => {
    const val = e.target.value;
    setActiveSearchContainer(e.currentTarget);
    setSearchValue(val);
    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/home/search-suggestions?searchValue=${encodeURIComponent(val.trim())}`);
        if (data.suggestions && data.suggestions.length > 0) {
          setSuggestions(data.suggestions);
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  }, [setActiveSearchContainer]);

  const handleSearchFocus = useCallback(e => {
    setActiveSearchContainer(e.currentTarget);
    if (searchValue.trim() && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [searchValue, suggestions.length, setActiveSearchContainer]);

  const handleClearSearch = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    clearTimeout(debounceRef.current);
    setIsClearingSearch(true);
    setSearchValue("");
    setSuggestions([]);
    setShowSuggestions(false);

    setTimeout(() => {
      setIsClearingSearch(false);
    }, 360);
  }, []);

  const getImageUrl = useCallback((suggestion) => {
    if (suggestion?.image) return suggestion.image;
    if (suggestion?.images && Array.isArray(suggestion.images) && suggestion.images.length > 0) {
      return suggestion.images[0];
    }
    return 'https://via.placeholder.com/80x80?text=Sin+Imagen';
  }, []);

  const formatPrice = useCallback((price) => {
    if (!price) return null;
    return new Intl.NumberFormat('es-PE', { 
      style: 'currency', 
      currency: 'PEN',
      minimumFractionDigits: 2 
    }).format(price);
  }, []);

  const handleSuggestionClick = useCallback((suggestion) => {
    setNavKey(prev => prev + 1);
    setShowSuggestions(false);
    setSuggestions([]);
    setSearchValue("");
    navigate(`/product/details/${suggestion.slug}`);
  }, [navigate]);

  const renderSuggestions = useCallback(() => {
    if (!suggestions.length || !showSuggestions) return null;
    if (dropdownPosition.width === 0) return null;
    
    const dropdown = (
      <div 
        ref={suggestionsDropdownRef}
        className="fixed bg-white border-2 border-gray-300 rounded-lg shadow-2xl overflow-y-auto"
        style={{ 
          zIndex: 999999,
          left: `${dropdownPosition.left}px`,
          top: `${dropdownPosition.top + 2}px`,
          width: `${dropdownPosition.width}px`,
          maxHeight: '400px'
        }}>
        {suggestions.map((suggestion, idx) => (
          <div
            key={`${suggestion.slug}-${idx}`}
            onClick={() => handleSuggestionClick(suggestion)}
            onMouseDown={(e) => e.preventDefault()}
            className="flex items-center gap-3 p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
          >
            <div className="w-14 h-14 flex-shrink-0 bg-gray-100 rounded overflow-hidden border border-gray-300">
              <img
                src={getImageUrl(suggestion)}
                alt={suggestion.name || 'Producto'}
                className="w-full h-full object-cover"
                onError={(e) => { 
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/80x80?text=Sin+Imagen'; 
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {suggestion.name || 'Sin nombre'}
              </p>
              {suggestion.price && (
                <p className="text-sm text-green-600 font-bold mt-1">
                  {formatPrice(suggestion.price)}
                </p>
              )}
              {suggestion.discount > 0 && (
                <span className="inline-block text-xs bg-red-500 text-white px-2 py-1 rounded mt-1">
                  -{suggestion.discount}%
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    );
    
    return createPortal(dropdown, document.body);
  }, [suggestions, showSuggestions, dropdownPosition, getImageUrl, formatPrice, handleSuggestionClick]);

  // Función para buscar productos (con recarga forzada)
  const handleSearch = useCallback(() => {
    if (searchValue.trim()) {
      setNavKey(prev => prev + 1);
      setShowSuggestions(false);
      setSuggestions([]);
      navigate(`/products/search?value=${encodeURIComponent(searchValue.trim())}`);
      setSearchValue("");
    }
  }, [searchValue, navigate]);

  // Handler para la tecla Enter
  const handleKeyPress = useCallback(
    e => {
      if (e.key === "Enter") {
        handleSearch();
      }
    },
    [handleSearch]
  );

  const redirect_card_page = () => {
    if (userInfo || guestId) {
      navigate("/carrito");
    } else {
      navigate("/login");
    }
  };

  const loadUserData =useCallback( userId => {
    dispatch(get_card_products(userId));
    dispatch(get_wishlist_products(userId));
  },[dispatch]);

  const loadGuestData =useCallback( guestId => {
    dispatch(get_card_products(guestId));
    dispatch(get_wishlist_products(guestId));
  }, [dispatch]);

  useEffect(() => {
    if (userInfo) {
      loadUserData(userInfo.id);
    } else if (guestId) {
      loadGuestData(guestId);
    }
  }, [userInfo, guestId, dispatch, loadUserData, loadGuestData]);

  // Construir navLinks desde Redux (navMenus)
  const navLinks = navMenus.map(menu => ({
    to: menu.slug_ruta,
    label: menu.etiqueta,
    esExterno: menu.es_externo,
    targetBlank: menu.abrir_en === "blank",
    badge: menu.texto_badge || "",
    colorTexto: menu.color_texto || "",
    colorFondo: menu.color_fondo || "",
    anchoSubmenu: menu.ancho_submenu || "full",
    alineacionSubmenu: menu.alineacion_submenu || "center",
    details: (menu.detalles || [])
      .filter(detail => detail.es_activo !== false)
      .map(detail => ({
        ...detail,
        links: (detail.links || [])
          .filter(link => link.es_activo !== false)
          .sort((a, b) => Number(a.orden_visual || 0) - Number(b.orden_visual || 0)),
      }))
      .sort((a, b) => Number(a.orden_visual || 0) - Number(b.orden_visual || 0)),
    hasMegaMenu: (menu.ver_submenu || menu.tipo_menu === "mega_menu") && (menu.detalles || []).some(detail => detail.es_activo !== false),
  }));

  const cancelMegaClose = useCallback(() => {
    clearTimeout(megaCloseRef.current);
  }, []);

  const scheduleMegaClose = useCallback(() => {
    clearTimeout(megaCloseRef.current);
    megaCloseRef.current = setTimeout(() => setActiveMegaMenu(null), 140);
  }, []);

  const openMegaMenu = useCallback(link => {
    clearTimeout(megaCloseRef.current);
    if (!link.hasMegaMenu) {
      setActiveMegaMenu(null);
      return;
    }

    const rect = navMenuRef.current?.getBoundingClientRect();
    setMegaTop(rect ? rect.bottom : 0);
    setActiveMegaMenu(link);
  }, []);

  const renderMegaLink = useCallback((item, className, children, options = {}) => {
    const to = options.to ?? item.slug_ruta ?? item.to ?? item.imagen_link ?? "";
    const external = options.external ?? item.es_externo ?? item.esExterno ?? isExternalUrl(to);

    if (!to) {
      return <span className={className}>{children}</span>;
    }

    if (external) {
      return (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={() => setActiveMegaMenu(null)}>
          {children}
        </a>
      );
    }

    return (
      <button
        type="button"
        onClick={() => {
          setActiveMegaMenu(null);
          handleNavClick(to);
        }}
        className={className}>
        {children}
      </button>
    );
  }, [handleNavClick]);

  const renderMegaMenu = useCallback(() => {
    if (!activeMegaMenu?.hasMegaMenu) return null;

    const alignClass = {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end",
    }[activeMegaMenu.alineacionSubmenu] || "justify-center";

    const widthClass = activeMegaMenu.anchoSubmenu === "auto"
      ? "w-[min(960px,calc(100vw-32px))]"
      : "w-[85%] lg:w-[90%]";

    return createPortal(
      <>
        <div
          className="fixed left-0 right-0 bottom-0 bg-black bg-opacity-40"
          style={{ top: `${megaTop}px`, zIndex: 99980 }}
          onMouseEnter={scheduleMegaClose}
        />
        <div
          className="fixed left-0 right-0 bg-white border-t border-gray-200 shadow-2xl"
          style={{ top: `${megaTop}px`, zIndex: 99990 }}
          onMouseEnter={cancelMegaClose}
          onMouseLeave={scheduleMegaClose}>
          <div className={`mx-auto flex ${alignClass}`}>
            <div className={`${widthClass} px-4 py-6`}>
              <div
                className="grid gap-6"
                style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
                {activeMegaMenu.details.map(detail => (
                  <div key={detail._id} className="min-w-0">
                    {detail.tipo_bloque === "imagen" ? (
                      <div>
                        {detail.imagen_url && renderMegaLink(
                          detail,
                          "block overflow-hidden rounded-md border border-gray-200 bg-gray-50 hover:shadow-card transition-shadow",
                          <img
                            src={detail.imagen_url}
                            alt={detail.imagen_alt || detail.titulo || activeMegaMenu.label}
                            className="w-full aspect-[4/3] object-cover"
                          />,
                          { to: detail.imagen_link || detail.slug_ruta, external: detail.es_externo || isExternalUrl(detail.imagen_link || detail.slug_ruta) }
                        )}
                        {detail.titulo && renderMegaLink(
                          detail,
                          "mt-3 block text-left text-sm font-semibold text-gray-900 hover:text-custom-rosado",
                          detail.titulo
                        )}
                      </div>
                    ) : (
                      <div>
                        {detail.titulo && renderMegaLink(
                          detail,
                          "block text-left text-sm font-bold text-gray-900 hover:text-custom-rosado mb-3",
                          detail.titulo
                        )}
                        <ul className="space-y-2">
                          {(detail.links || []).map(link => (
                            <li key={link._id || `${detail._id}-${link.etiqueta}`}>
                              {renderMegaLink(
                                link,
                                `block w-full text-left text-sm leading-5 transition-colors ${
                                  link.es_oferta ? "text-custom-rosado font-semibold" : "text-gray-600 hover:text-custom-rosado"
                                }`,
                                link.etiqueta
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </>,
      document.body
    );
  }, [activeMegaMenu, megaTop, renderMegaLink, cancelMegaClose, scheduleMegaClose]);

  const renderSidebarMenuLink = useCallback((item, key, className) => {
    const to = item.to || item.slug_ruta || item.imagen_link || "";
    const label = item.label || item.etiqueta || item.titulo || item.imagen_alt || "Ver mas";
    const external = item.esExterno || item.es_externo || item.targetBlank || isExternalUrl(to);

    if (!to) {
      return <span key={key} className={className}>{label}</span>;
    }

    if (external) {
      return (
        <a
          key={key}
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setShowSidebar(false)}
          className={className}>
          {label}
        </a>
      );
    }

    return (
      <button
        key={key}
        onClick={() => {
          handleNavClick(to);
          setShowSidebar(false);
        }}
        className={className}>
        {label}
      </button>
    );
  }, [handleNavClick]);

  // Si currentCompany es null, mostrar un esqueleto de carga
  if (!currentCompany) {
    return <div className="w-full bg-white sticky top-0 z-50 shadow-md overflow-x-hidden">{/* ... skeleton loading igual ... */}</div>;
  }

  return (
    <>
      {/* Spacer para compensar el fixed cuando hay scroll */}
      {scrolled && <div className="h-[108px] md:h-[110px]" />}

      <div className={`w-full bg-white overflow-x-hidden z-50 ${
        scrolled ? "fixed top-0 left-0 right-0 shadow-lg" : "relative shadow-header"
      }`}>
      {/* Header Top - Desktop: se oculta al hacer scroll */}
      <div className={`header-top md-lg:hidden overflow-hidden transition-all duration-300 ${
        scrolled ? "hidden" : ""
      }`} style={{ backgroundColor: headerTopBg }}>
        <div className="w-[85%] lg:w-[90%] mx-auto max-w-full">
          <div className="flex w-full justify-between items-center h-[28px] text-slate-500 min-w-0">
            <ul className="flex justify-start items-center gap-8 font-semibold text-black min-w-0 flex-shrink">
              {phone && (
                <li className="flex relative justify-center items-center gap-2 text-sm flex-shrink-0">
                  <span>
                    <IoMdPhonePortrait />
                  </span>
                  <Link to={`tel:+${phone_whatsApp}`} id="menu-enlaces-fonocompras" title="Comuníquese con Fonocompras">
                    <span>{phone}</span>
                  </Link>
                </li>
              )}

              <li className="flex relative justify-center items-center gap-2 text-sm flex-shrink-0">
                <Link to="/atencion-al-cliente" id="menu-enlaces-atencion" title="Atención al cliente">
                  <span>SERVICIO AL CLIENTE</span>
                </Link>
              </li>

              <li className="flex relative justify-center items-center gap-2 text-sm flex-shrink-0">
                <Link to="/seguimiento-pedido" id="menu-enlaces-seguimiento" title="Sigue tu pedido">
                  <span>SEGUIMIENTO DE PEDIDOS</span>
                </Link>
              </li>

              <li className="flex relative justify-center items-center gap-2 text-sm flex-shrink-0">
                <Link to={dashboardUrl} id="menu-enlaces-creatumarketplace" title="Crea tu marketplace" target="_blank" rel="noopener noreferrer">
                  <span>Crea tu marketplace</span>
                </Link>
              </li>
            </ul>

            <div className="flex-shrink-0">
              <div className="flex justify-center items-center gap-10">
                <div className="flex justify-center items-center gap-4 text-black">
                  <a href="https://www.facebook.com/share/1FkgDAhPGx/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">
                    <FaFacebookF />{" "}
                  </a>
                  <a href="https://www.instagram.com/tekos.peru?igsh=MXg0bzZ6ZHAwY2l1aQ%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer">
                    <FaInstagram />{" "}
                  </a>
                  <a href="https://www.tiktok.com/@tekos.peru?_t=ZM-8wlJsEKl0vm&_r=1" target="_blank" rel="noopener noreferrer">
                    <FaTiktok />{" "}
                  </a>
                </div>

                {userInfo ? (
                  <Link to="/dashboard" className="flex cursor-pointer justify-center items-center gap-2 text-sm text-black flex-shrink-0">
                    <span>
                      {" "}
                      <FaUser />{" "}
                    </span>{" "}
                    <span> {userInfo.name} </span>
                  </Link>
                ) : (
                  <Link to="/login" className="flex cursor-pointer justify-center items-center gap-2 text-sm text-black flex-shrink-0">
                    <span>
                      {" "}
                      <FaLock />{" "}
                    </span>{" "}
                    <span>Acceso </span>{" "}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Header (oculto en móvil según tu tailwind invertido) */}
      <div className="md:hidden overflow-hidden">
        <div className="w-full max-w-full mx-auto" style={{ ...getBackground("background"), boxShadow: "0 6px 20px 0 rgba(164,28,31,0.45), 0 2px 6px 0 rgba(164,28,31,0.25)" }}>
          <div className="h-18 flex justify-between items-center px-4">
            {/* Logo a la izquierda */}
            <div className="w-3/12 min-w-0">
              <Link to="/" className="text-2xl font-bold text-custom-rosado block">
                <img className="max-w-full h-[60px] sm:h-[50px] object-contain" src={logo} alt={name} />
              </Link>
            </div>

            {/* Barra de búsqueda centrada */}
            <div className="w-6/12 px-2 min-w-0">
              <div className="relative w-full" data-search-container>
                <div className="flex border border-gray-300 h-12 items-center relative rounded-lg overflow-hidden w-full">
                  <input
                    className="w-full text-gray-700 outline-none pl-4 pr-24 h-full placeholder-gray-400"
                    onChange={handleSearchChange}
                    type="text"
                    placeholder="Buscar productos..."
                    onKeyPress={handleKeyPress}
                    value={searchValue}
                    onFocus={handleSearchFocus}
                  />
                  {(searchValue || isClearingSearch) && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-16 z-10 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                      style={{ color: primaryColor }}
                      aria-label="Limpiar busqueda">
                      <FaTimes className={isClearingSearch ? "search-clear-spin" : ""} />
                    </button>
                  )}
                  <button onClick={handleSearch} style={getButtonStyles("primary", "white")} className="absolute right-0 px-6 h-full font-semibold transition-colors">
                    <FaSearch />
                  </button>
                </div>
              </div>
            </div>

            {/* Iconos de usuario a la derecha */}
            <div className="w-3/12 flex justify-end items-center gap-3 min-w-0">
              <div
                onClick={() => navigate(userInfo ? "/dashboard/my-wishlist" : "/login")}
                className="relative flex justify-center items-center cursor-pointer w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                <FaHeart className="text-lg sm:text-xl text-custom-rosado" />
                {wishlist_count !== 0 && (
                  <div style={{ ...getBackground("background") }} className="w-4 h-4 absolute rounded-full text-white flex justify-center items-center -top-1 -right-1 text-xs">
                    {wishlist_count}
                  </div>
                )}
              </div>

              <div
                onClick={redirect_card_page}
                className="relative flex justify-center items-center cursor-pointer w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                <FaCartShopping className="text-lg sm:text-xl text-custom-rosado" />
                {card_product_count !== 0 && (
                  <div style={{ ...getBackground("background") }} className="w-4 h-4 absolute rounded-full text-white flex justify-center items-center -top-1 -right-1 text-xs">
                    {card_product_count}
                  </div>
                )}
              </div>

              {userInfo ? (
                <Link
                  to="/dashboard"
                  className="flex justify-center items-center cursor-pointer w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                  <FaUser className="text-lg sm:text-xl text-custom-rosado" />
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="flex justify-center items-center cursor-pointer w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                  <FaUser className="text-lg sm:text-xl text-custom-rosado" />
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Menú de navegación desktop */}
        <div ref={navMenuRef} className="border-t border-gray-200">
          <div className="w-full max-w-full mx-auto flex justify-center items-center h-10 px-4">
            <ul className="flex justify-center items-center gap-4 sm:gap-6 text-sm font-medium min-w-0 overflow-x-auto whitespace-nowrap scrollbar-hide">
              {navLinks.map((link, index) => (
                <li
                  key={`${link.to}-${index}-${navKey}`}
                  className="flex-shrink-0"
                  onMouseEnter={() => openMegaMenu(link)}
                  onMouseLeave={scheduleMegaClose}>
                  {link.esExterno ? (
                    <a
                      href={link.to}
                      target={link.targetBlank || isExternalUrl(link.to) ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      style={{
                        color: link.colorTexto || undefined,
                        backgroundColor: link.colorFondo || undefined,
                      }}
                      className="p-2 flex items-center gap-1 hover:text-custom-rosado transition-colors border-b-2 hover:border-custom-rosado pb-1 text-custom-slate-500 border-transparent">
                      <span>{link.label}</span>
                      {link.badge && <span className="text-[10px] text-red-600 font-bold">{link.badge}</span>}
                      {link.hasMegaMenu && <FaChevronDown className="text-[10px]" />}
                    </a>
                  ) : (
                    <button
                      onClick={() => {
                        if (link.targetBlank) {
                          window.open(link.to, "_blank", "noopener,noreferrer");
                          return;
                        }
                        handleNavClick(link.to);
                      }}
                      style={{
                        color: link.colorTexto || undefined,
                        backgroundColor: link.colorFondo || undefined,
                      }}
                      className={`p-2 flex items-center gap-1 hover:text-custom-rosado transition-colors border-b-2 hover:border-custom-rosado pb-1 ${
                        isLinkActive(link.to) ? "text-custom-rosado border-custom-rosado" : "text-custom-slate-500 border-transparent"
                      }`}>
                      <span>{link.label}</span>
                      {link.badge && <span className="text-[10px] text-red-600 font-bold">{link.badge}</span>}
                      {link.hasMegaMenu && <FaChevronDown className="text-[10px]" />}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Mobile Header (visible en móvil) */}
      <div style={{ ...getBackground("background"), boxShadow: "0 6px 20px 0 rgba(164,28,31,0.45), 0 2px 6px 0 rgba(164,28,31,0.25)" }} className="md:block hidden w-full overflow-hidden">
        <div className="w-full max-w-full mx-auto py-2 px-3">
          {/* Fila superior: Logo a la izquierda y menú hamburguesa a la derecha */}
          <div className="flex justify-between items-center mb-3 w-full min-w-0">
            {/* Logo más pequeño alineado a la izquierda */}
            <div className="flex-1 min-w-0">
              <Link to="/" className="text-2xl font-bold text-black block max-w-full">
                <img src={logo} alt={name} className="h-10 w-auto object-contain max-w-full" />
              </Link>
            </div>

            {/* Iconos de usuario a la derecha */}
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              <div
                onClick={() => navigate(userInfo ? "/dashboard/my-wishlist" : "/login")}
                className="relative flex justify-center items-center cursor-pointer w-8 h-8 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                <FaHeart className="text-lg text-custom-rosado" />
                {wishlist_count !== 0 && (
                  <div style={{ ...getBackground("background") }} className="w-4 h-4 absolute rounded-full text-white flex justify-center items-center -top-1 -right-1 text-xs">
                    {wishlist_count}
                  </div>
                )}
              </div>

              <div
                onClick={redirect_card_page}
                className="relative flex justify-center items-center cursor-pointer w-8 h-8 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                <FaCartShopping className="text-lg text-custom-rosado" />
                {card_product_count !== 0 && (
                  <div style={{ ...getBackground("background") }} className="w-4 h-4 absolute rounded-full text-white flex justify-center items-center -top-1 -right-1 text-xs">
                    {card_product_count}
                  </div>
                )}
              </div>

              {userInfo ? (
                <Link to="/dashboard" className="flex justify-center items-center cursor-pointer w-8 h-8 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                  <FaUser className="text-lg text-custom-rosado" />
                </Link>
              ) : (
                <Link to="/login" className="flex justify-center items-center cursor-pointer w-8 h-8 rounded-full bg-custom-slate-100 hover:bg-gray-200 transition-colors flex-shrink-0">
                  <FaUser className="text-lg text-custom-rosado" />
                </Link>
              )}

              {/* Menú hamburguesa */}
              <button onClick={() => setShowSidebar(true)} className="flex items-center justify-center w-8 h-8 text-white flex-shrink-0">
                <FaBars className="text-xl" />
              </button>
            </div>
          </div>

          {/* Barra de búsqueda debajo (ocupa todo el ancho) */}
          <div className="w-full mb-2">
            <div className="relative w-full" data-search-container>
              <div className="flex border border-gray-300 h-10 items-center relative rounded-full overflow-hidden w-full">
                <input
                  className="w-full text-gray-700 outline-none pl-4 pr-20 h-full placeholder-gray-400 text-sm"
                  onChange={handleSearchChange}
                  type="text"
                  placeholder="Buscar productos..."
                  value={searchValue}
                  onKeyPress={handleKeyPress}
                  onFocus={handleSearchFocus}
                />
                {(searchValue || isClearingSearch) && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-12 z-10 flex h-7 w-7 items-center justify-center rounded-full transition-colors hover:bg-gray-100"
                    style={{ color: primaryColor }}
                    aria-label="Limpiar busqueda">
                    <FaTimes className={isClearingSearch ? "search-clear-spin" : ""} />
                  </button>
                )}
                <button onClick={handleSearch} style={getButtonStyles("primary", "white")} className="absolute right-0 px-4 h-full">
                  <FaSearch />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
      {showSuggestions && renderSuggestions()}
      {renderMegaMenu()}

      {showSidebar && (
        <div className="fixed inset-0 z-50 md:block hidden">
          {/* Fondo oscuro */}
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowSidebar(false)}></div>

          {/* Panel lateral */}
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-lg">
            <div className="p-4">
              {/* Encabezado del sidebar */}
              <div className="flex justify-between items-center mb-6">
                <span className="font-bold text-lg">{name}</span>
                <button onClick={() => setShowSidebar(false)} className="p-1 rounded-full hover:bg-gray-100">
                  <FaTimes className="text-gray-500" />
                </button>
              </div>

              {/* Menú de navegación */}
              <nav className="flex flex-col space-y-3">
                {navLinks.map((link, index) => (
                  <div key={`sidebar-${link.to}-${index}`} className="border-b border-gray-100 pb-3 last:border-b-0">
                    {renderSidebarMenuLink(
                      link,
                      `sidebar-main-${link.to}-${index}`,
                      `p-2 w-full flex items-center justify-between text-left ${
                        isLinkActive(link.to) ? "text-custom-rosado font-medium" : "text-gray-700"
                      } hover:text-custom-rosado transition-colors`
                    )}
                    {link.hasMegaMenu && (
                      <div className="ml-3 mt-2 flex flex-col gap-2">
                        {link.details.map(detail => (
                          <div key={`sidebar-detail-${detail._id}`} className="flex flex-col gap-1">
                            {detail.titulo && renderSidebarMenuLink(
                              detail,
                              `sidebar-detail-title-${detail._id}`,
                              "px-2 py-1 text-left text-xs font-semibold text-gray-900 hover:text-custom-rosado"
                            )}
                            {detail.tipo_bloque === "imagen" ? (
                              detail.imagen_url && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const to = detail.imagen_link || detail.slug_ruta;
                                    if (to && (detail.es_externo || isExternalUrl(to))) {
                                      window.open(to, "_blank", "noopener,noreferrer");
                                    } else if (to) {
                                      handleNavClick(to);
                                    }
                                    setShowSidebar(false);
                                  }}
                                  className="mx-2 overflow-hidden rounded border border-gray-200">
                                  <img
                                    src={detail.imagen_url}
                                    alt={detail.imagen_alt || detail.titulo || link.label}
                                    className="h-20 w-full object-cover"
                                  />
                                </button>
                              )
                            ) : (
                              (detail.links || []).map(subLink => renderSidebarMenuLink(
                                subLink,
                                `sidebar-sub-${detail._id}-${subLink._id || subLink.etiqueta}`,
                                `px-2 py-1 text-left text-sm ${
                                  subLink.es_oferta ? "text-custom-rosado font-semibold" : "text-gray-600"
                                } hover:text-custom-rosado`
                              ))
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
